import { useEffect, useState } from "react";
import Select from "react-select";
import { Formik, Form, Field } from "formik";
import { object, string, date } from "yup";
import { toast } from "react-toastify";
import CloseIcon from "../../../assets/hospitalCloseIcon.svg";
import RightArrow from "../../../assets/rightArrowWhite.svg";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

const errorValidation = object({
  status: string().required("Required Field"),
  statustime: date().required("Required Field"),
});

const dischargeOptions = [
  { value: "Death", label: "Death" },
  { value: "Discharge", label: "Discharge" },
  { value: "LAMA", label: "LAMA" },
  { value: "DAMA", label: "DAMA" },
  { value: "REFER", label: "REFER" },
];

/**
 * TPA Discharge step 1 (details) — mirrors admission ReferenceNotUploadedModal.
 * Upload opens a separate modal via onContinueToUpload.
 */
const TpaDischargeFormHospitalPersona = ({
  caseId,
  setAddDischarge,
  onContinueToUpload,
}) => {
  const [dischargeDetails, setDischargeDetails] = useState(null);
  const [initialValues, setInitialValues] = useState({
    status: "",
    statustime: "",
  });

  const getDischarge = async () => {
    try {
      const response = await CaseServices.getDischarge(caseId);
      setDischargeDetails(response?.data?.dischargeDetails?.details);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const handleUploadReferenceClick = async (
    values,
    validateForm,
    setTouched,
  ) => {
    const errors = await validateForm();
    setTouched({
      status: true,
      statustime: true,
    });
    if (Object.keys(errors || {}).length) {
      toast.error("Please fill all required fields");
      return;
    }
    onContinueToUpload?.(values);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    getDischarge();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (dischargeDetails) {
      setInitialValues(dischargeDetails);
    } else {
      const now = new Date();
      const tzOffset = now.getTimezoneOffset() * 60000;
      const localISOTime = new Date(now - tzOffset).toISOString().slice(0, 16);
      setInitialValues((prev) => ({
        ...prev,
        statustime: localISOTime,
      }));
    }
  }, [dischargeDetails]);

  return (
    <div className="fixed top-0 left-0 w-screen h-screen flex justify-center items-center bg-[#017C7180] z-[1200]">
      <div className="relative h-fit w-[50%] max-w-[90vw] bg-white p-[30px] rounded-[30px] overflow-y-auto overflow-x-hidden max-h-[90vh] box-border">
        <div
          className="absolute top-2 right-2 cursor-pointer"
          onClick={() => setAddDischarge(false)}
        >
          <img src={CloseIcon} alt="close-icon" />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={errorValidation}
          onSubmit={() => {}}
          enableReinitialize
        >
          {({
            errors,
            touched,
            setFieldValue,
            values,
            validateForm,
            setTouched,
          }) => (
            <Form>
              <div className="text-[40px] font-medium text-black -tracking-[7%]">
                Discharge
              </div>
              <div
                style={{
                  borderTop: "1px solid #969696",
                  marginBlock: "25px",
                }}
              />

              <div className="w-full flex flex-col gap-y-8">
                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    1. Please select discharge
                  </div>
                  <Select
                    name="status"
                    placeholder="Select Discharge"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "100px",
                      }),
                    }}
                    options={dischargeOptions}
                    value={
                      dischargeOptions.find(
                        (option) => option.value === values.status,
                      ) || null
                    }
                    onChange={(item) => setFieldValue("status", item.value)}
                  />
                  {touched.status && errors.status && (
                    <div className="field-error">{errors.status}</div>
                  )}
                </div>

                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    2. Please select the date & time for Discharge
                  </div>
                  <Field
                    type="datetime-local"
                    name="statustime"
                    style={{ border: "1px solid #E4E4E4" }}
                    className="rounded-full py-[10px] px-2 w-full"
                  />
                  {touched.statustime && errors.statustime && (
                    <div className="field-error">{errors.statustime}</div>
                  )}
                </div>

                <div className="flex justify-center items-center mt-2">
                  <button
                    type="button"
                    style={{ border: "1px solid #017C71" }}
                    className="flex items-center gap-4 text-sm text-white bg-[#017C71] -tracking-[3%] uppercase rounded-full px-10 py-4 border-0 cursor-pointer"
                    onClick={() =>
                      handleUploadReferenceClick(
                        values,
                        validateForm,
                        setTouched,
                      )
                    }
                  >
                    Upload Reference Document
                    <img src={RightArrow} alt="" />
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default TpaDischargeFormHospitalPersona;
